import { TOKEN_STORAGE_KEY } from './config';

// Eager session-expiry handler.
//
// Without this, the browser only learns the session is dead when the
// next API call returns 401 — meaning a stale dashboard could sit on
// screen for many minutes. The watcher decodes the JWT `exp` claim and
// fires a timer at the exact instant the token expires; on fire we
// clear the token, drop any cached query data, and bounce to
// /login?reason=expired so LoginPage can surface a notice.

let expiryTimer: ReturnType<typeof setTimeout> | null = null;
const expirySubscribers = new Set<() => void>();
let watcherInstalled = false;

/** Decode the `exp` claim from a JWT. Returns ms-epoch or null on bad input. */
export function decodeJwtExp(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    // base64url → base64
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    const json = JSON.parse(window.atob(b64 + pad)) as { exp?: unknown };
    return typeof json.exp === 'number' ? json.exp * 1000 : null;
  } catch {
    return null;
  }
}

/** True if the token's exp claim is in the past (or unreadable). */
export function isTokenExpired(token: string): boolean {
  const expMs = decodeJwtExp(token);
  if (expMs === null) return false;     // unreadable — let the server decide
  return expMs <= Date.now();
}

/** (Re-)arm the expiry timer for the given token. Safe to call repeatedly. */
export function scheduleExpiry(token: string): void {
  if (typeof window === 'undefined') return;
  if (expiryTimer !== null) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
  const expMs = decodeJwtExp(token);
  if (expMs === null) return;
  const delta = Math.max(0, expMs - Date.now());
  // setTimeout clamps to int32; if a deploy ever issues a 25-day token
  // we'd silently miss the fire. Clamp it ourselves at 24h so the
  // timer at least re-arms when the user returns to the tab.
  const clamped = Math.min(delta, 24 * 60 * 60 * 1000);
  expiryTimer = setTimeout(fireExpiry, clamped);
}

/** Cancel any pending expiry timer. Called on explicit logout. */
export function cancelExpiry(): void {
  if (expiryTimer !== null) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
}

/** Subscribe to expiry fires (used to clear the React Query cache). */
export function subscribeExpiry(cb: () => void): () => void {
  expirySubscribers.add(cb);
  return () => { expirySubscribers.delete(cb); };
}

/** Tear it all down — clear token, notify subscribers, redirect to /login.
 *  reason: 'expired' (token exp / absolute TTL) or 'idle' (inactivity
 *  timeout — System Settings → Session). */
export function fireExpiry(reason: 'expired' | 'idle' = 'expired'): void {
  if (typeof window === 'undefined') return;
  cancelExpiry();
  try { window.sessionStorage.removeItem(TOKEN_STORAGE_KEY); } catch { /* sessionStorage may be blocked */ }
  for (const cb of expirySubscribers) {
    try { cb(); } catch { /* a subscriber must not block the redirect */ }
  }
  if (!window.location.pathname.startsWith('/login')) {
    window.location.replace(`/login?reason=${reason}`);
  }
}

/**
 * Install the cross-tab + cross-event watcher. Safe to call multiple times
 * (only the first call wires the listeners). Call from App.tsx after the
 * QueryClient is constructed.
 *
 * - `visibilitychange` — when the tab regains focus we re-check `exp`. The
 *   setTimeout can have been throttled to seconds-coarse by a background
 *   tab; the visibility check catches the case where the token went stale
 *   while we weren't looking.
 * - `storage`           — when another tab logs out, we follow.
 */
export function installExpiryWatcher(): void {
  if (typeof window === 'undefined' || watcherInstalled) return;
  watcherInstalled = true;

  // Re-check on tab focus.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const token = window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (token && isTokenExpired(token)) fireExpiry();
    else if (token) scheduleExpiry(token);   // re-arm in case the timer was throttled
  });

  // Sync across tabs — though sessionStorage is per-tab, we still listen
  // for safety in case a future change moves the token to localStorage.
  window.addEventListener('storage', (ev) => {
    if (ev.key === TOKEN_STORAGE_KEY && ev.newValue === null) {
      // Other tab cleared the token; redirect ourselves too.
      if (!window.location.pathname.startsWith('/login')) {
        window.location.replace('/login?reason=expired');
      }
    }
  });

  // Boot: arm the timer if a token is already in storage (page reload case).
  const token = window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    if (isTokenExpired(token)) fireExpiry();
    else scheduleExpiry(token);
  }
}
